import { overrideLabel, type StateRuleRow } from '@fundline/shared/compliance/rules';
import { formatLocalTimeOfDay as formatTimeOfDay } from '@fundline/shared/compliance/display';
import { parseTimeOfDay } from '@fundline/shared/compliance/window';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL, SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { SettingsTabs } from '../settings/SettingsTabs.tsx';
import {
  checkForm,
  describeOverride,
  EMPTY_FORM,
  type FormState,
  toForm,
} from './sending-hours-form.ts';
import {
  type OverrideRecord,
  useComplianceReferenceQuery,
  useStateOverrideMutations,
  useStateOverridesQuery,
} from './use-sending-rules.ts';

/**
 * Settings → Sending hours (M7). The base windows from the spec, the platform's state rules (read-only,
 * each marked "Not verified by counsel" until the lawyer review), the federal holidays, and the
 * workspace's own rules, which can only be stricter: the worker intersects them with everything else.
 */
export function SendingHoursPage() {
  const { workspaceId } = useWorkspaceClaims();
  const reference = useComplianceReferenceQuery();
  const overrides = useStateOverridesQuery(workspaceId);
  const error = reference.error ?? overrides.error;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="When texts go out, in each merchant's own time zone. Opt-out confirmations and HELP replies always go out at once."
      />
      <SettingsTabs />
      {error ? <Alert tone="error">{error.message}</Alert> : null}
      {reference.isPending || overrides.isPending ? <Spinner /> : null}
      {reference.data && overrides.data ? (
        <>
          <BaseRules />
          <WorkspaceRules
            overrides={overrides.data}
            stateRules={[...reference.data.stateRules.values()]}
          />
          <StateRules rules={[...reference.data.stateRules.values()]} />
          <Holidays />
        </>
      ) : null}
    </div>
  );
}

function BaseRules() {
  return (
    <Card title="Base sending hours">
      <dl className="callout grid grid-cols-1 gap-x-4 gap-y-2 text-ink sm:grid-cols-[auto_1fr]">
        <dt className="font-semibold">Campaigns and sequences</dt>
        <dd>Mon–Fri 9:00 AM–7:00 PM, Sat 10:00 AM–2:00 PM. No Sundays or federal holidays.</dd>
        <dt className="font-semibold">Replies and one-to-one texts</dt>
        <dd>8:00 AM–9:00 PM every day.</dd>
      </dl>
      <p className="footnote mt-3 text-ink-muted">
        Always the merchant&apos;s local time. Nothing goes out in the first or last 2 minutes of a
        window. When the state and the area code point to different places, both apply. Texts
        outside these hours wait and go out when the window opens.
      </p>
    </Card>
  );
}

function WorkspaceRules({
  overrides,
  stateRules,
}: {
  overrides: OverrideRecord[];
  stateRules: StateRuleRow[];
}) {
  const { workspaceId } = useWorkspaceClaims();
  const { save, remove } = useStateOverrideMutations(workspaceId);
  const [editing, setEditing] = useState<{ id: string | null; form: FormState } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const names = new Map(stateRules.map((r) => [r.state, r.name]));
  const taken = new Set(overrides.map((o) => o.state ?? ''));

  const checked = editing ? checkForm(editing.form) : null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!editing || !checked?.input) return;
    save.mutate(
      { id: editing.id, input: checked.input },
      {
        onSuccess: () => {
          setEditing(null);
          setSubmitted(false);
        },
      },
    );
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    save.reset();
    setEditing((cur) => (cur ? { ...cur, form: { ...cur.form, [key]: value } } : cur));
  };

  return (
    <Card title="Your workspace's rules">
      <p className="callout mb-3 text-ink-muted">
        Make the hours stricter for every state or for one. These rules can only take time away;
        they never allow texts the base hours or a state&apos;s rule would stop.
      </p>
      {overrides.length === 0 && !editing ? (
        <EmptyState title="No workspace rules">
          The base hours and the state rules apply.
        </EmptyState>
      ) : null}
      {overrides.length > 0 ? (
        <ul className="mb-3 flex flex-col divide-y divide-hairline" aria-label="Workspace rules">
          {overrides.map((o) => (
            <li
              key={o.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
              data-testid="override-row"
            >
              <div className="min-w-0">
                <p className="callout font-semibold text-ink">
                  {overrideLabel(o, names).replace('Workspace: ', '')}
                </p>
                <p className="footnote text-ink-muted">{describeOverride(o)}</p>
                {o.notes ? <p className="footnote text-ink-muted">{o.notes}</p> : null}
              </div>
              <div className="flex gap-2">
                {confirmRemove === o.id ? (
                  <>
                    <Button
                      variant="danger"
                      size="sm"
                      busy={remove.isPending}
                      onClick={() =>
                        remove.mutate(o.id, { onSuccess: () => setConfirmRemove(null) })
                      }
                    >
                      Remove rule
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(null)}>
                      Keep
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSubmitted(false);
                        setEditing({ id: o.id, form: toForm(o) });
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(o.id)}>
                      Remove
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {remove.error ? <Alert tone="error">{remove.error.message}</Alert> : null}

      {editing ? (
        <form
          className="flex flex-col gap-3 rounded-md bg-surface p-3"
          onSubmit={onSubmit}
          noValidate
          aria-label="Workspace rule"
        >
          <SelectField
            label="Applies to"
            value={editing.form.state}
            disabled={editing.id !== null}
            onChange={(e) => set('state', e.target.value)}
            options={[
              ...(editing.id !== null || !taken.has('')
                ? [{ value: '', label: 'All states' }]
                : []),
              ...stateRules
                .filter((r) => editing.id !== null || !taken.has(r.state))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((r) => ({ value: r.state, label: r.name })),
            ]}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TimeField
              label="Earliest (merchant's time)"
              value={editing.form.start}
              onChange={(v) => set('start', v)}
            />
            <TimeField
              label="Latest (merchant's time)"
              value={editing.form.end}
              onChange={(v) => set('end', v)}
            />
          </div>
          <label className="callout flex items-start gap-2 text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-5 accent-brand"
              checked={editing.form.noSundays}
              onChange={(e) => set('noSundays', e.target.checked)}
            />
            No texts on Sundays
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Most texts per 24 hours"
              inputMode="numeric"
              value={editing.form.cap}
              onChange={(e) => set('cap', e.target.value)}
              hint="Per merchant. Leave empty for no limit."
            />
            <SelectField
              label="Count"
              value={editing.form.campaignOnly ? 'campaign' : 'all'}
              onChange={(e) => set('campaignOnly', e.target.value === 'campaign')}
              options={[
                { value: 'all', label: 'Every text' },
                { value: 'campaign', label: 'Campaign and sequence texts only' },
              ]}
            />
          </div>
          <TextField
            label="Note"
            value={editing.form.notes}
            maxLength={500}
            onChange={(e) => set('notes', e.target.value)}
            hint="Why this rule exists (optional)."
          />
          {checked?.noEffect ? (
            <p className="footnote text-warning">
              This rule changes nothing: the base hours are already stricter.
            </p>
          ) : null}
          {submitted && checked?.error ? <Alert tone="error">{checked.error}</Alert> : null}
          {save.error ? <Alert tone="error">{save.error.message}</Alert> : null}
          <div className="flex gap-2">
            <Button type="submit" busy={save.isPending}>
              Save rule
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="secondary"
          onClick={() => {
            setSubmitted(false);
            save.reset();
            setEditing({
              id: null,
              form: {
                ...EMPTY_FORM,
                state: taken.has('')
                  ? (stateRules.find((r) => !taken.has(r.state))?.state ?? '')
                  : '',
              },
            });
          }}
        >
          Add a rule
        </Button>
      )}
    </Card>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = `time-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="callout font-semibold text-ink">
        {label}
      </label>
      <input
        id={id}
        type="time"
        step={60}
        className={CONTROL}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function StateRules({ rules }: { rules: StateRuleRow[] }) {
  const stricter = rules
    .filter(
      (r) => r.allowedStart || r.allowedEnd || r.sundayStart || !r.sundayAllowed || r.maxPer24h,
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card title="State rules">
      <p className="callout mb-3 text-ink-muted">
        Set by Fundline from public summaries of state law. They are checked by a lawyer before
        go-live; until then each one says so.
      </p>
      <ul className="flex flex-col divide-y divide-hairline" aria-label="State rules">
        {stricter.map((r) => (
          <li key={r.state} className="flex flex-col gap-1 py-2" data-testid="state-rule">
            <div className="flex flex-wrap items-center gap-2">
              <p className="callout font-semibold text-ink">{r.name}</p>
              {r.verifiedByCounsel ? (
                <Badge role="funded">Verified by counsel</Badge>
              ) : (
                <Badge role="pending">Not verified by counsel</Badge>
              )}
            </div>
            <p className="footnote text-ink-muted">
              {describeOverride({
                allowedStart: r.allowedStart,
                allowedEnd: r.allowedEnd,
                sundayAllowed: r.sundayAllowed,
                maxPer24h: r.maxPer24h,
                capAppliesTo: r.capAppliesTo,
              })}
              {r.sundayStart
                ? ` · Sundays from ${formatTimeOfDay(parseTimeOfDay(r.sundayStart) ?? 0)}`
                : ''}
            </p>
            {r.notes ? <p className="footnote text-ink-muted">{r.notes}</p> : null}
          </li>
        ))}
      </ul>
      <p className="footnote mt-2 text-ink-muted">Every other state: the base hours.</p>
    </Card>
  );
}

function Holidays() {
  const holidays = useQuery({
    queryKey: [...queryKeys.complianceReference(), 'holidays'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase()
        .from('federal_holidays')
        .select('holiday_date, name')
        .gte('holiday_date', today)
        .order('holiday_date')
        .limit(14);
      if (error) throw new Error(`Couldn't load the holidays: ${error.message}`);
      return data;
    },
  });
  return (
    <Card title="Federal holidays">
      <p className="callout mb-3 text-ink-muted">
        No campaign or sequence texts. When a holiday falls on a weekend, both the day it&apos;s
        observed and the day itself are closed.
      </p>
      {holidays.isPending ? <Spinner /> : null}
      {holidays.error ? <Alert tone="error">{holidays.error.message}</Alert> : null}
      <ul className="callout flex flex-col gap-1 text-ink" aria-label="Upcoming federal holidays">
        {(holidays.data ?? []).map((h) => (
          <li key={h.holiday_date} className="flex justify-between gap-3">
            <span>{h.name}</span>
            <span className="fl-num text-ink-muted">
              {new Date(`${h.holiday_date}T12:00:00Z`).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
